import { motion } from 'framer-motion';
import {
  Award, RotateCcw, XCircle, CheckCircle2, ArrowLeft,
  Download, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';

const EVAL_CRITERIA = [
  { key: 'satisfaction', label: 'Satisfaction globale' },
  { key: 'content_quality', label: 'Qualite du contenu' },
  { key: 'clarity', label: 'Clarté des explications' },
  { key: 'applicability', label: 'Applicabilite au poste' }
];

const EMOJI_SCALE = [
  { value: 1, emoji: '\u{1F621}', label: 'Très insatisfait' },
  { value: 2, emoji: '\u{1F615}', label: 'Insatisfait' },
  { value: 3, emoji: '\u{1F610}', label: 'Neutre' },
  { value: 4, emoji: '\u{1F642}', label: 'Satisfait' },
  { value: 5, emoji: '\u{1F929}', label: 'Très satisfait' }
];

export const CourseCompletionScreen = ({
  completionResult, evalStep, evalRatings, evalComment,
  evalSubmitting, certDownloading,
  onSetEvalRatings, onSetEvalComment, onSubmitEvaluation,
  onRestart, onDownloadCert, onNavigateBack
}) => (
  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-xl mx-auto text-center" data-testid="completion-screen">
    {/* FAILED */}
    {!completionResult.passed && (
      <div className="rounded-2xl p-10 bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
          <XCircle className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-red-800 mb-2" data-testid="completion-title">Score insuffisant</h2>
        <p className="text-red-700 mb-1">Votre score</p>
        <p className="text-4xl font-black text-red-600 mb-1" data-testid="completion-score">{completionResult.overall_score}%</p>
        <p className="text-sm text-red-500 mb-2">Minimum requis : 70%</p>
        <p className="text-sm text-slate-600 mb-6">Vous devez recommencer la formation depuis le debut pour obtenir votre attestation.</p>
        <Button onClick={onRestart} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-restart-course">
          <RotateCcw className="w-4 h-4 mr-2" /> Recommencer la formation
        </Button>
      </div>
    )}

    {/* Evaluation form */}
    {completionResult.passed && evalStep === 'form' && (
      <div className="rounded-2xl p-8 bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 text-left" data-testid="evaluation-form">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3">
            <Award className="w-8 h-8 text-teal-600" />
          </div>
          <h2 className="text-xl font-bold text-teal-800">Formation validee - {completionResult.overall_score}%</h2>
          <p className="text-sm text-teal-600 mt-1">Avant de recevoir votre attestation, merci d'evaluer cette formation</p>
        </div>
        <div className="space-y-5">
          {EVAL_CRITERIA.map(({ key, label }) => (
            <div key={key} data-testid={`eval-${key}`}>
              <p className="text-sm font-semibold text-slate-700 mb-2">{label}</p>
              <div className="flex gap-2">
                {EMOJI_SCALE.map(({ value, emoji, label: emojiLabel }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onSetEvalRatings(prev => ({ ...prev, [key]: value }))}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all flex-1 ${
                      evalRatings[key] === value
                        ? 'bg-teal-100 ring-2 ring-teal-400 scale-110'
                        : 'bg-white hover:bg-slate-50 border border-slate-200'
                    }`}
                    title={emojiLabel}
                    data-testid={`eval-${key}-${value}`}
                  >
                    <span className="text-2xl">{emoji}</span>
                    <span className="text-[9px] text-slate-500 leading-tight">{emojiLabel}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div data-testid="eval-comment">
            <p className="text-sm font-semibold text-slate-700 mb-2">Commentaire (facultatif)</p>
            <textarea
              value={evalComment}
              onChange={(e) => onSetEvalComment(e.target.value)}
              rows={3}
              placeholder="Vos remarques, suggestions..."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-y focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
              data-testid="eval-comment-input"
            />
          </div>
          <Button
            onClick={onSubmitEvaluation}
            disabled={evalSubmitting || !evalRatings.satisfaction || !evalRatings.content_quality || !evalRatings.clarity || !evalRatings.applicability}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="btn-submit-eval"
          >
            {evalSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Soumettre l'évaluation et obtenir mon attestation
          </Button>
        </div>
      </div>
    )}

    {/* Certificate download */}
    {completionResult.passed && evalStep === 'done' && (
      <div className="rounded-2xl p-10 bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200" data-testid="certificate-screen">
        <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
          <Award className="w-10 h-10 text-teal-600" />
        </div>
        <h2 className="text-2xl font-bold text-teal-800 mb-2" data-testid="completion-title">Felicitations !</h2>
        <p className="text-teal-700 mb-1">Vous avez obtenu</p>
        <p className="text-4xl font-black text-teal-700 mb-1" data-testid="completion-score">{completionResult.overall_score}%</p>
        <p className="text-sm text-teal-600 mb-6">Votre attestation de formation est prete</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={onDownloadCert} disabled={certDownloading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="btn-download-cert">
            {certDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Télécharger l'attestation PDF
          </Button>
          <Button variant="outline" onClick={onNavigateBack} className="border-teal-300 text-teal-700" data-testid="btn-back-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'espace client
          </Button>
        </div>
      </div>
    )}
  </motion.div>
);
